import React, { useState, useEffect } from "react";
import { Input } from "../../components/ui/input2";
import { Button } from "../../components/ui/Button";
import { Globe, Info, AlertCircle, CheckCircle2, Lock } from "lucide-react";
import SectionHeader from "../../components/ui/SectionHeader";
import { useOutletContext } from "react-router-dom";
import { SettingsOutletContextType } from "../Settings";
import { verifyDomain, installDomainSsl } from "../../services/settings";
import { useToast } from "../../hooks/useToast";
import GlobalLoader from "../../components/ui/GlobalLoader";
import PageHeader from "../../components/ui/PageHeader";

const AttachDomainSettings = () => {
  const toast = useToast();
  const { settingsData, refreshSettings } = useOutletContext<SettingsOutletContextType>();

  const [domain, setDomain] = useState("");
  const [error, setError] = useState("");
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [isInstallingSsl, setIsInstallingSsl] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (settingsData && settingsData.settings) {
      setDomain(settingsData.settings.custom_domain || "");
      if (settingsData.settings.custom_domain_status === "verified") {
      }
    }
  }, [settingsData]);

  const validateDomain = (value: string): boolean => {
    setError("");
    setSuccessMessage("");

    if (!value.trim()) {
      setError("Domain is required");
      return false;
    }

    if (
      value.toLowerCase().includes("http://") ||
      value.toLowerCase().includes("https://")
    ) {
      setError("Please remove 'http://' or 'https://' from the domain");
      return false;
    }

    if (value.toLowerCase().startsWith("www.")) {
      setError("Please remove 'www.' from the domain");
      return false;
    }
    const domainRegex =
      /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/;
    if (!domainRegex.test(value)) {
      setError("Please enter a valid domain (e.g., example.com)");
      return false;
    }

    return true;
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDomain(value);
    if (error || successMessage) {
      setError("");
      setSuccessMessage("");
    }
  };

  const handleVerifyAndSave = async () => {
    if (!validateDomain(domain)) {
      return;
    }

    setIsValidating(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await verifyDomain(domain);
      if (response.success) {
        // Check for specific "verified" keyword to be sure, otherwise assume check completed but might be pending
        const msg = response.message?.toLowerCase() || "";
        const isVerified = msg.includes("successfully") && !msg.includes("pending") && !msg.includes("completed");

        if (isVerified) {
          toast.success("Domain verified successfully!");
        } else {
          // Fallback for "completed" or "pending" or other success states that aren't explicit verifications
          toast.info("Verification check completed. Current status: " + (msg.includes("pending") ? "Pending" : "See badge"));
        }
        refreshSettings();
      } else {
        const errorMsg = response.message || "Verification failed.";
        toast.error(errorMsg);
      }
    } catch (err: any) {
      toast.error("Could not verify domain. Please check your DNS settings and try again.");
    } finally {
      setIsValidating(false);
    }
  };

  const handleInstallSsl = async () => {
    if (!settingsData?.settings?.id) {
      return;
    }
    setIsInstallingSsl(true);
    try {
      const response = await installDomainSsl(settingsData.settings.id);
      if (response.success) {
        setSuccessMessage(response.message || "SSL installation started.");
        toast.success("SSL installation started!");
      } else {
        setError(response.message || "SSL installation failed.");
        toast.error("SSL installation could not be started. Please try again later.");
      }
    } catch (err: any) {
      setError("An error occurred installing SSL.");
      toast.error("SSL installation failed. Please try again later.");
    } finally {
      setIsInstallingSsl(false);
    }
  };

  const currentStatus = settingsData?.settings?.custom_domain_status;

  if (!settingsData) {
    return (
      <div className="flex items-center justify-center p-12 min-h-[400px]">
        <GlobalLoader
          initialPageLoad={false}
          description="Loading domain settings..."
          compensateSidebar={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Attach 3rd Party Domain"
        description="Connect your custom domain to your application"
        variant="card"
      />

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-full flex flex-col">
        {/* Card Header */}
        <div className="bg-[#885ABB] px-6 py-3">
          <h3 className="text-base font-semibold text-white flex items-center gap-2">
            <Globe size={18} className="text-white" />
            Domain Configuration
          </h3>
        </div>

        {/* Card Body */}
        <div className="p-5 flex-1 ">
          <div className="space-y-5">
            {/* Domain Instructions Section */}
            <div>
              <SectionHeader title="Domain Configuration" />
            </div>
            {/* DNS Instruction Box */}
            <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-100 flex items-start gap-3">
              <Info size={20} className="text-[#885ABB] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-purple-900">
                To verify your domain, please add the IP address <span className="font-semibold">'168.119.170.197'</span> to your DNS A Record using the alias <span className="font-semibold">'@'</span>.
              </p>
            </div>

            {/* Domain Input Section */}
            <div>
              <SectionHeader title="Domain Verification" />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Domain <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={domain}
                    onChange={handleDomainChange}
                    placeholder="example.com"
                    className={`flex-1 ${error
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/40"
                      : ""
                      }`}
                  />
                  {/* Status badge if exists */}
                  {currentStatus && (
                    <div className={`px-3 py-2 rounded-md border text-sm font-medium flex items-center capitalize
                      ${currentStatus === 'active' || currentStatus === 'verified' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'}`}>
                      {currentStatus}
                    </div>
                  )}
                </div>

                {/* Helper Text */}
                <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                  <Info size={12} />
                  Enter only the domain name (e.g., example.com) - no http://,
                  https://, or www.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end pt-2 gap-3">
              {/* Install SSL Button - show if Active/Verified */}
              {(currentStatus === 'active' || currentStatus === 'verified') && (
                <Button
                  onClick={handleInstallSsl}
                  disabled={isInstallingSsl}
                  variant="outline"
                  className="px-6 py-2 text-sm font-medium border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {isInstallingSsl ? "Installing..." : <><Lock size={16} className="mr-2" /> Install SSL</>}
                </Button>
              )}

              <Button
                onClick={handleVerifyAndSave}
                disabled={isValidating || !domain.trim()}
                className={`px-8 py-2 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200 ${isValidating || !domain.trim()
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-[#885ABB] hover:bg-[#7748A4] text-white"
                  }`}
              >
                {isValidating ? "Verifying..." : "Verify & Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachDomainSettings;
